import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { subscribeAsk, type AskRequest } from "@/api/ask-store";
import { useBrandId } from "@/state/brand";
import { ChatView } from "../chat/ChatView";
import { IconButton } from "../ui/Button";
import { RobotFace } from "../ui/Page";
import { Drawer } from "../ui/Overlay";

const ANALYTICS = /\/(home|monitor|performance|decisions|check)(\/|$)/;

/** Floating "Ask Mimic" button on analytics pages + the chat drawer it opens (with page context). */
export function AskAgent() {
  const loc = useLocation();
  const nav = useNavigate();
  const brandId = useBrandId();
  const [open, setOpen] = useState(false);
  const [req, setReq] = useState<AskRequest>({});
  const [threadId, setThreadId] = useState<string | null>(null);
  const [session, setSession] = useState(0);

  useEffect(
    () =>
      subscribeAsk((r) => {
        setReq(r);
        setThreadId(null);
        setSession((s) => s + 1);
        setOpen(true);
      }),
    [],
  );

  const show = ANALYTICS.test(loc.pathname);
  const context = { page: loc.pathname, search: loc.search, ...req.context };

  return (
    <>
      {show && !open && (
        <button
          type="button"
          onClick={() => {
            setReq({});
            setOpen(true);
          }}
          className="fixed bottom-36 right-2 z-30 flex flex-col items-center gap-1 md:bottom-[5.25rem] md:right-[1.25rem]"
          aria-label="Ask Mimic"
        >
          <span className="grid size-13 place-items-center rounded-full bg-primary shadow-[0_8px_30px_-6px_rgba(86,67,225,0.7)] transition-transform hover:scale-105">
            <RobotFace color="#ffffff" eyes="#5643e1" className="size-7" />
          </span>
          <span className="rounded-full bg-black/60 px-1.5 text-[10.5px] font-medium text-fg/90">Ask Mimic</span>
        </button>
      )}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="md"
        title={
          <span className="flex items-center gap-2">
            <RobotFace color="#e08a3c" className="size-5" /> {req.title ?? "Ask Mimic"}
          </span>
        }
        subtitle="Mimic sees what's on this page. Spend, activation and deletes always ask first."
        className="[&>div:nth-child(2)]:p-0"
      >
        <div className="relative flex h-full min-h-[60vh] flex-col">
          {threadId && (
            <div className="absolute right-3 top-2 z-10">
              <IconButton
                label="Open full chat"
                onClick={() => {
                  setOpen(false);
                  nav(`/chat/${threadId}`);
                }}
              >
                <ExternalLink className="size-4" />
              </IconButton>
            </div>
          )}
          <ChatView key={session} compact brandId={brandId} context={context} initialPrompt={req.prompt} autoSend={req.send} threadId={threadId} onThreadCreated={setThreadId} />
        </div>
      </Drawer>
    </>
  );
}
